@if (isLoading) {
  <lib-loading-indicator [isLoading]="isLoading"></lib-loading-indicator>
}
<div
  class="projectable-form-wrapper frontdoor-form ipsec-form-page"
  (keyup.escape)="closeModal(false)"
  >
  <lib-form-header
    [data]="formData"
    [title]="isExistingEntity ? 'View IPsec Tunnel: ' : 'Create New IPsec Tunnel'"
    [moreActions]="moreActions"
    [showHeaderButton]="!isEditing"
    (actionRequested)="headerActionRequested($event)"
    [showHeaderToggle]="false"
  ></lib-form-header>
  <div class="integration-form-container projectable-form-container ipsec-form-body">
      <!-- Create only. In view mode the tab strip already names what is on the page, so a lead
           line just pushes the content down. -->
      @if (!isEditing) {
        <p class="ipsec-form-lead form-group-row">
          Define the peer gateway (public IP and routed CIDR) for this tunnel.
        </p>
      }
      <div class="ipsec-tunnel-layout form-group-row">

        <!-- Tabs only in view mode. Creating a tunnel is a single short form, and hiding half of
             it behind a tab would be worse than the scroll it saves. -->
        @if (isEditing) {
          <div class="tab-nav" role="tablist">
            <button type="button" role="tab" class="tab" [class.active]="activeTab === 'overview'"
                    (click)="setTab('overview')">Overview</button>
            <button type="button" role="tab" class="tab" [class.active]="activeTab === 'ipsec'"
                    (click)="setTab('ipsec')">IPsec</button>
            <button type="button" role="tab" class="tab" [class.active]="activeTab === 'metrics'"
                    (click)="setTab('metrics')">Metrics</button>
          </div>
        }

        @if (!isEditing || activeTab === 'overview') {
        <div class="tunnel-grid">
          <div class="form-group-column tunnel-col">
            <div class="ipsec-form-panel">
              <lib-form-field-container [title]="'Peer & routing'">
                <div [ngClass]="isEditing ? 'field-grid' : 'stacked-fields'">

                  <div class="field-row">
                    <div class="field-label">
                      Name
                      @if (!isEditing) { <span class="field-label-suffix">REQUIRED</span> }
                    </div>
                    @if (!isEditing) {
                      <input
                        class="form-field-input ipsec-form-input"
                        id="IpsecTunnelName"
                        (keydown.enter)="$event.preventDefault(); onFieldEnter('IpsecTunnelName')"
                        placeholder="e.g. customer-a-dc1"
                        [ngClass]="{error: hasError('name')}"
                        [(ngModel)]="formData.name"
                        autofocus
                      />
                    }
                    @if (isEditing) {
                      <span class="field-value">{{ formData.name }}</span>
                    }
                  </div>

                  <div class="field-row">
                    <div class="field-label">
                      Peer Public IP
                      <div class="form-field-help-icon" matTooltip="The public IPv4 address of your on-premises VPN gateway. This is the internet-facing IP that will be used to establish the IPsec tunnel." matTooltipPosition="below">?</div>
                      @if (!isEditing) { <span class="field-label-suffix">REQUIRED</span> }
                    </div>
                    @if (!isEditing) {
                      <input
                        class="form-field-input ipsec-form-input"
                        id="IpsecTunnelPeerPublicIp"
                        (keydown.enter)="$event.preventDefault(); onFieldEnter('IpsecTunnelPeerPublicIp')"
                        placeholder="e.g. 203.0.113.10"
                        [ngClass]="{error: hasError('peerPublicIp')}"
                        [(ngModel)]="formData.peerPublicIp"
                      />
                      @if (errMap['peerPublicIpInvalid'] && !errorText('peerPublicIp')) {
                        <span class="field-error">Must be a valid IPv4 address</span>
                      }
                      @if (errorText('peerPublicIp')) {
                        <span class="field-error">{{ errorText('peerPublicIp') }}</span>
                      }
                    }
                    @if (isEditing) {
                      <span class="field-value field-value--mono">{{ formData.peerPublicIp }}</span>
                    }
                  </div>

                  <div class="field-row">
                    <div class="field-label">
                      Peer CIDRs
                      <div class="form-field-help-icon" matTooltip="The private network ranges behind your VPN gateway that should be reachable through the tunnel (e.g. 172.31.0.0/16). Traffic destined for these CIDRs will be routed through the IPsec connection. Add a row for each subnet." matTooltipPosition="below">?</div>
                      @if (!isEditing) { <span class="field-label-suffix">REQUIRED</span> }
                    </div>
                    @if (!isEditing) {
                      <div class="cidr-chips-container" id="IpsecTunnelPeerCidrs"
                           [class.error]="hasError('peerCidrs')">
                        @for (cidr of peerCidrs; track cidr) {
                          <span class="cidr-chip" [id]="'IpsecTunnelPeerCidrChip_' + cidr">
                            {{ cidr }}
                            <span
                              class="chip-remove"
                              (click)="removeCidr(cidr)"
                              [attr.aria-label]="'Remove ' + cidr"
                            >&times;</span>
                          </span>
                        }
                        <input
                          type="text"
                          class="form-field-input cidr-input"
                          [(ngModel)]="peerCidrInput"
                          (keydown.enter)="$event.preventDefault(); onPeerCidrEnter()"
                          placeholder="e.g. 172.31.0.0/16"
                          id="IpsecTunnelPeerCidrInput"
                        />
                        <button
                          type="button"
                          class="add-button"
                          (click)="addCidr(peerCidrInput)"
                          id="IpsecTunnelPeerCidrAdd"
                        >+</button>
                      </div>
                      @if (errMap['peerCidrs']) {
                        <span class="field-error">{{ errMap['peerCidrs'] }}</span>
                      }
                    }
                    @if (isEditing) {
                      <div class="cidr-list">
                        @for (cidr of formData.peerCidrs; track cidr) {
                          <span class="field-value field-value--mono">{{ cidr }}</span>
                        }
                        @if (!formData.peerCidrs?.length) {
                          <span class="field-value field-value--mono">{{ formData.peerCidr || '—' }}</span>
                        }
                      </div>
                    }
                  </div>

                </div>

                  @if (!isEditing) {
                    <div class="field-row">
                      <div class="field-label">
                        Pre-Shared Key
                        <div class="form-field-help-icon" matTooltip="The secret both ends use to authenticate the tunnel. Generate one unless your device is already configured with a key, or your process requires the key to come from your side." matTooltipPosition="below">?</div>
                      </div>
                      <!-- A radio group rather than two buttons: arrow keys move between the
                           options, and only the selected one is in the tab order, so Tab passes
                           the control once instead of stopping on each half. -->
                      <div class="btn-group psk-mode" role="radiogroup" aria-label="Pre-shared key source"
                           (keydown.arrowleft)="$event.preventDefault(); movePskMode(-1)"
                           (keydown.arrowright)="$event.preventDefault(); movePskMode(1)"
                           (keydown.arrowup)="$event.preventDefault(); movePskMode(-1)"
                           (keydown.arrowdown)="$event.preventDefault(); movePskMode(1)">
                        <button type="button" class="btn-group-item"
                                id="IpsecTunnelPskModeGenerate"
                                role="radio"
                                [attr.aria-checked]="pskMode === 'generate'"
                                [attr.tabindex]="pskMode === 'generate' ? 0 : -1"
                                [class.active]="pskMode === 'generate'"
                                (keydown.enter)="$event.preventDefault(); onFieldEnter('IpsecTunnelPskModeGenerate')"
                                (click)="setPskMode('generate')">Generate</button>
                        <button type="button" class="btn-group-item"
                                id="IpsecTunnelPskModeProvide"
                                role="radio"
                                [attr.aria-checked]="pskMode === 'provide'"
                                [attr.tabindex]="pskMode === 'provide' ? 0 : -1"
                                [class.active]="pskMode === 'provide'"
                                (keydown.enter)="$event.preventDefault(); onFieldEnter('IpsecTunnelPskModeProvide')"
                                (click)="setPskMode('provide')">Provide</button>
                      </div>
                      @if (pskMode === 'generate') {
                        <span class="field-hint">A key is generated when the tunnel is created. It appears in the setup document.</span>
                      }
                      @if (pskMode === 'provide') {
                        <div class="psk-entry">
                          <input
                            class="form-field-input ipsec-form-input"
                            [type]="pskRevealed ? 'text' : 'password'"
                            [(ngModel)]="pskInput"
                            id="IpsecTunnelPsk"
                            (keydown.enter)="$event.preventDefault(); onFieldEnter('IpsecTunnelPsk')"
                            [ngClass]="{error: errMap['psk']}"
                            placeholder="20-128 characters, no spaces"
                            autocomplete="off"
                            spellcheck="false"
                            aria-label="Pre-shared key"
                          />
                          <button type="button" class="psk-reveal"
                                  (click)="togglePskReveal()"
                                  [attr.aria-label]="pskRevealed ? 'Hide pre-shared key' : 'Show pre-shared key'"
                                  [matTooltip]="pskRevealed ? 'Hide' : 'Show'" matTooltipPosition="above">
                            <i class="pi" [class.pi-eye]="!pskRevealed" [class.pi-eye-slash]="pskRevealed" aria-hidden="true"></i>
                          </button>
                        </div>
                        @if (errMap['psk']) {
                          <span class="field-error">{{ errMap['psk'] }}</span>
                        }
                        @if (!errMap['psk']) {
                          <span class="field-hint">No spaces, quotes, backslashes or {{ '{{' }} {{ '}}' }} — the bridge cannot carry them.</span>
                        }
                      }
                    </div>
                  }
              </lib-form-field-container>
            </div>
          </div>

          <div class="form-group-column tunnel-col">
            <div class="ipsec-form-panel ipsec-form-panel--accent">
              <lib-form-field-container [title]="'Hosting Details'">
                <div [ngClass]="isEditing ? 'field-grid' : 'stacked-fields'">
                  @if (isEditing) {
                    <div class="field-row">
                      <div class="field-label">Status</div>
                      <span class="status-pill" [attr.data-status]="(formData.status || '').toLowerCase()">{{ formData.status }}</span>
                    </div>
                    <div class="field-row">
                      <div class="field-label">Tunnel Availability</div>
                      @if (uptimePercent !== null) {
                        <span class="uptime-badge"
                          [class.uptime-good]="uptimePercent >= 99"
                          [class.uptime-warn]="uptimePercent >= 95 && uptimePercent < 99"
                          [class.uptime-bad]="uptimePercent < 95"
                        >{{ uptimePercent | number:'1.1-1' }}%</span>
                      }
                      @if (uptimePercent === null) {
                        <span class="field-value">—</span>
                      }
                    </div>
                    <div class="field-row">
                      <div class="field-label">Bridge Public IP
                        <div class="form-field-help-icon" matTooltip="The public IP address of this tunnel's bridge. Configure this as the remote peer address in your on-premise IPsec gateway." matTooltipPosition="below">?</div>
                      </div>
                      <span class="field-value field-value--mono">{{ formData.publicIp || '—' }}</span>
                    </div>
                    <div class="field-row">
                      <div class="field-label">Bridge Private IP
                        <div class="form-field-help-icon" matTooltip="The private IP address of this tunnel's bridge. This is the entry point for traffic routed from IPsec shares into the NetFoundry network." matTooltipPosition="below">?</div>
                      </div>
                      <span class="field-value field-value--mono">{{ formData.privateIp || '—' }}</span>
                    </div>
                    <div class="field-row">
                      <div class="field-label">Region</div>
                      <span class="field-value field-value--mono">{{ formData.region || '—' }}</span>
                    </div>
                  }
                  @if (!isEditing) {
                    <div class="field-label-info ipsec-form-callout">
                      Hosting status and internal IP information will become available after the tunnel is provisioned.
                    </div>
                  }
                </div>
              </lib-form-field-container>
            </div>
          </div>
        </div>
        }

        @if (isEditing && activeTab === 'metrics') {
          <div class="tunnel-grid">
            <div class="form-group-column" style="flex: 1 1 100%; min-width: 0; width: 100%;">
              <div class="ipsec-form-panel">
                <lib-form-field-container [title]="'Data Transfer'">
                  <div class="metrics-controls">
                    <span class="metrics-label">Window:</span>
                    <div class="btn-group">
                      <button
                        *ngFor="let w of ['30m', '1h', '1d', '7d']"
                        type="button"
                        class="btn-group-item"
                        [class.active]="metricsWindow === w"
                        (click)="onWindowChange(w)"
                      >{{ w }}</button>
                    </div>
                    <app-refresh-controls
                      subject="metrics"
                      [loading]="metricsLoading"
                      [seconds]="metricsRefreshSeconds"
                      (refresh)="refreshMetrics()"
                      (secondsChange)="setMetricsRefresh($event)"
                    />
                  </div>
                  <div class="charts-grid">
                    <div class="chart-card">
                      <div class="chart-title">Throughput (rx / tx)</div>
                      <app-rx-tx-timeline
                        [samples]="metricSamples"
                        [downtimeRanges]="downtimeRanges"
                        [markers]="rekeyMarkers"
                        [loading]="metricsLoading"
                        height="14rem"
                        unit="bytes"
                        rxLabel="Rx"
                        txLabel="Tx"
                      ></app-rx-tx-timeline>
                      <div class="chart-footnote">Red bands mark IKE_SA (Phase 1) outages; amber dashed lines mark CHILD_SA (Phase 2) rekey moments (hover for the child id). Brief line gaps without either marker are scrape misses.</div>
                    </div>
                    <div class="chart-card">
                      <div class="chart-title">Liveness (seconds since last in / out)</div>
                      <app-rx-tx-timeline
                        [samples]="livenessSamples"
                        [downtimeRanges]="downtimeRanges"
                        [markers]="rekeyMarkers"
                        [loading]="metricsLoading"
                        height="12rem"
                        unit="seconds"
                        rxLabel="In"
                        txLabel="Out"
                      ></app-rx-tx-timeline>
                    </div>
                    <div class="chart-card">
                      <div class="chart-title">Bridge (cpu / mem %)</div>
                      <app-rx-tx-timeline
                        [samples]="systemSamples"
                        [downtimeRanges]="downtimeRanges"
                        [markers]="rekeyMarkers"
                        [loading]="metricsLoading"
                        height="10rem"
                        unit="percent"
                        rxLabel="CPU"
                        txLabel="Mem"
                      ></app-rx-tx-timeline>
                    </div>
                  </div>
                </lib-form-field-container>
              </div>
            </div>
          </div>
        }

        @if (isEditing && activeTab === 'ipsec') {
          <div class="tunnel-grid">
            <div class="form-group-column" style="flex: 1 1 100%; min-width: 0; width: 100%;">
              <div class="ipsec-form-panel" [class.stale-content]="isStale">
                <lib-form-field-container [title]="'IPsec Snapshot'">
                  @if (!hasReported) {
                    <div class="empty-state-card">
                      <h3>Waiting for first report</h3>
                      <p>This tunnel's bridge hasn't reported yet. The VM may still be provisioning.</p>
                      <p class="hint">Reopen this tab in a minute to check again.</p>
                    </div>
                  }
                  @if (hasReported) {
                    <!-- Grouped by what the reader is asking: the three phase-1 fields answer
                         "is the tunnel up", the two host gauges answer "is the bridge healthy".
                         Each group shares a line when there is room and stacks when there isn't. -->
                    <div class="tab-stack bridge-snapshot">
                      <div class="field-grid">
                        <div class="field-row">
                          <div class="field-label">IKE State (Phase 1)</div>
                          <span class="field-value">{{ bridgeHealth?.ikeState || 'UNKNOWN' }}</span>
                        </div>
                        <div class="field-row">
                          <div class="field-label">IKEs Connected (Phase 1)</div>
                          <span class="field-value">{{ bridgeHealth?.ikesConnected ?? 0 }}</span>
                        </div>
                        <div class="field-row">
                          <div class="field-label">IKE Established (Phase 1)</div>
                          <span class="field-value">{{ formatEpochMs(bridgeHealth?.ikeEstablishedAt) }}</span>
                        </div>
                      </div>
                      <div class="field-grid">
                        <div class="field-row">
                          <div class="field-label">Bridge CPU</div>
                          <span class="field-value">{{ formatPct(bridgeHealth?.cpuPct) }}</span>
                        </div>
                        <div class="field-row">
                          <div class="field-label">Bridge Memory</div>
                          <span class="field-value">{{ formatPct(bridgeHealth?.memPct) }}</span>
                        </div>
                        <div class="field-row">
                          <div class="field-label">Last Seen</div>
                          <span class="field-value">{{ formatEpochMs(bridgeHealth?.lastSeenAt) }}</span>
                        </div>
                      </div>
                    </div>
                  }
                </lib-form-field-container>
              </div>
            </div>
          </div>

          <div class="tunnel-grid">
            <div class="form-group-column" style="flex: 1 1 100%; min-width: 0; width: 100%;">
              <div class="ipsec-form-panel">
                <lib-form-field-container [title]="'Child SAs (Phase 2)'">
                  @if (hasChildSAs()) {
                    <div class="child-sa-scroll">
                      <table class="child-sa-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>State</th>
                            <th>Bytes In</th>
                            <th>Bytes Out</th>
                            <th>Established</th>
                            <th>Next Rekey</th>
                            <th>Last In (s)</th>
                            <th>Last Out (s)</th>
                            <th>Local TS</th>
                            <th>Remote TS</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (sa of bridgeHealth?.childSAs; track sa.childId) {
                            <tr>
                              <td>{{ sa.childName || '—' }}</td>
                              <td>{{ sa.state || '—' }}</td>
                              <td>{{ formatBytes(sa.bytesIn) }}</td>
                              <td>{{ formatBytes(sa.bytesOut) }}</td>
                              <td>{{ formatEpochMs(sa.establishedAt) }}</td>
                              <td>{{ formatEpochMs(sa.rekeyAt) }}</td>
                              <td>{{ sa.lastInboundSeconds ?? '—' }}</td>
                              <td>{{ sa.lastOutboundSeconds ?? '—' }}</td>
                              <td>{{ (sa.localTrafficSelectors || []).join(', ') || '—' }}</td>
                              <td>{{ (sa.remoteTrafficSelectors || []).join(', ') || '—' }}</td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  }
                  @if (!hasChildSAs()) {
                    <div class="empty-state">No child SAs (Phase 2) reported.</div>
                  }
                </lib-form-field-container>
              </div>
            </div>
          </div>

          <div class="tunnel-grid">
            <div class="form-group-column" style="flex: 1 1 100%; min-width: 0; width: 100%;">
              <div class="ipsec-form-panel">
                <lib-form-field-container [title]="'Bridge Logs'">
                  <div class="events-controls">
                    <span class="metrics-label">Window:</span>
                    <div class="btn-group">
                      <button
                        *ngFor="let w of ['1h', '1d', '7d']"
                        type="button"
                        class="btn-group-item"
                        [class.active]="eventsWindow === w"
                        (click)="setEventsWindow(w)"
                      >{{ w }}</button>
                    </div>
                    <span class="metrics-label kind-label">Source:</span>
                    <div class="btn-group">
                      <button type="button" class="btn-group-item"
                              [class.active]="eventKindFilter === null"
                              (click)="setEventKindFilter(null)">All</button>
                      @for (k of eventKinds; track k) {
                        <button type="button" class="btn-group-item"
                                [class.active]="eventKindFilter === k"
                                (click)="setEventKindFilter(k)">{{ k }}</button>
                      }
                    </div>
                    <app-refresh-controls
                      subject="logs"
                      [loading]="eventsLoading"
                      [seconds]="eventsRefreshSeconds"
                      (refresh)="refreshEvents()"
                      (secondsChange)="setEventsRefresh($event)"
                    />
                  </div>
                  <div class="log-search">
                    <button type="button" class="log-search-go"
                            (click)="applyEventSearch()"
                            matTooltip="Search logs" matTooltipPosition="above"
                            aria-label="Search logs">
                      <i class="pi pi-search" aria-hidden="true"></i>
                    </button>
                    <input
                      type="text"
                      class="form-field-input log-search-input"
                      [(ngModel)]="eventSearchInput"
                      (keydown.enter)="$event.preventDefault(); applyEventSearch()"
                      placeholder="Filter log messages by word or phrase"
                      aria-label="Search log messages"
                    />
                    @if (eventSearch) {
                      <button type="button" class="log-search-clear"
                              (click)="clearEventSearch()"
                              matTooltip="Clear search" matTooltipPosition="above"
                              aria-label="Clear search">&times;</button>
                    }
                  </div>
                  @if (events.length > 0) {
                    <div class="events-scroll">
                      <table class="events-table">
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Source</th>
                            <th>Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (e of pagedEvents; track $index) {
                            <tr class="event-row">
                              <td>{{ formatEpochMs(e.t) }}</td>
                              <td>{{ e.kind }}</td>
                              <td class="event-message">{{ e.message }}</td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                    <div class="events-pager">
                      <button type="button" class="pager-btn" (click)="prevEventsPage()"
                              [disabled]="eventsPageIndex === 0">Prev</button>
                      <span class="pager-label">Page {{ eventsPageIndex + 1 }} of {{ eventsTotalPages }} ({{ events.length }} lines)</span>
                      <button type="button" class="pager-btn" (click)="nextEventsPage()"
                              [disabled]="eventsPageIndex >= eventsTotalPages - 1">Next</button>
                    </div>
                  }
                  @if (!eventsLoading && events.length === 0) {
                    <div class="empty-state">
                      No log lines in this range{{ eventKindFilter ? ' from ' + eventKindFilter : '' }}.
                    </div>
                  }
                </lib-form-field-container>
              </div>
            </div>
          </div>
        }
      </div>

  </div>
</div>
